export * from './lessonSessionNotifyable';

import { User, Subscription, ApiResponse, AppError } from '../types';
import type { Currency, Region } from '../types/region';
import { getLanguageFromRegion } from './locationDetection';

// Firebase utilities
export const initializeFirebase = (config: any) => {
  // This would be implemented with actual Firebase initialization
  console.log('Firebase initialized with config:', config);
};

// Authentication utilities
export const isAuthenticated = (user: User | null): boolean => {
  return user !== null;
};

export const hasActiveSubscription = (subscription: Subscription | null): boolean => {
  return subscription !== null && subscription.status === 'active';
};

export const isSubscriptionExpired = (subscription: Subscription): boolean => {
  return new Date() > subscription.currentPeriodEnd;
};

// Validation utilities
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

// API utilities
export const createApiResponse = <T>(
  data: T,
  message?: string
): ApiResponse<T> => ({
  success: true,
  data,
  ...(message && { message }),
});

export const createErrorResponse = (
  error: string
): ApiResponse => ({
  success: false,
  error,
});

export const handleApiError = (error: unknown): ApiResponse => {
  if (error instanceof AppError) {
    return createErrorResponse(error.message);
  }
  
  if (error instanceof Error) {
    return createErrorResponse(error.message);
  }
  
  return createErrorResponse('An unexpected error occurred');
};

// Date utilities
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateTime = (date: Date): string => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Parse `Date` or string for locale-aware display.
 * ISO date-only strings (YYYY-MM-DD) are treated as a local calendar date so they do not shift
 * across time zones when formatted (unlike `new Date('YYYY-MM-DD')`, which is UTC midnight).
 */
function coerceDateForDisplay(date: Date | string): Date {
  if (date instanceof Date) {
    return date;
  }
  const isoDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  if (isoDay) {
    const y = Number(isoDay[1]);
    const m = Number(isoDay[2]) - 1;
    const day = Number(isoDay[3]);
    return new Date(y, m, day);
  }
  return new Date(date);
}

/**
 * Format a date for display using the academy's region.
 * US: MM/DD/YYYY (e.g. 12/3/2025), AU: DD/MM/YYYY (e.g. 3/12/2025).
 * When region is undefined, defaults to US format.
 */
export const formatDateForRegion = (
  date: Date | string,
  region?: Region | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string => {
  const d = coerceDateForDisplay(date);
  const locale = getLanguageFromRegion(region ?? 'US');
  return d.toLocaleDateString(locale, options);
};

/**
 * Format date and time for display using the academy's region.
 * When region is undefined, defaults to US format.
 */
export const formatDateTimeForRegion = (
  date: Date | string,
  region?: Region | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
): string => {
  const d = coerceDateForDisplay(date);
  const locale = getLanguageFromRegion(region ?? 'US');
  return d.toLocaleString(locale, options);
};

export const getDaysUntilExpiry = (subscription: Subscription): number => {
  const now = new Date();
  const expiry = subscription.currentPeriodEnd;
  const diffTime = expiry.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Currency utilities
export const formatCurrency = (
  amount: number,
  currency: string = 'USD'
): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

/** Maps arbitrary currency strings to supported display currencies; unknown values fall back to USD. */
export function normalizeToDisplayCurrency(currencyRaw: string | null | undefined): Currency {
  const n = currencyRaw?.trim().toUpperCase();
  if (n === 'AUD' || n === 'GBP' || n === 'EUR' || n === 'USD') return n;
  return 'USD';
}

/** Uses {@link normalizeToDisplayCurrency}, then {@link formatCurrency} — safe for untrusted/legacy strings. */
export function formatCurrencySafe(amount: number, currencyRaw: string | null | undefined): string {
  return formatCurrency(amount, normalizeToDisplayCurrency(currencyRaw));
}

// String utilities
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Array utilities
export const chunk = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)];
};

// Object utilities
export const pick = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
};

export const omit = <T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach((key) => {
    delete result[key];
  });
  return result;
};

// Async utilities
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const retry = async <T>(
  fn: () => Promise<T>,
  maxAttempts: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      if (attempt === maxAttempts) {
        throw lastError;
      }
      await sleep(delay * attempt);
    }
  }
  
  throw lastError!;
};

// Processing fee (surcharge) utilities
export * from './processingFee';
export * from './surchargePassThroughAcknowledgment';

// Lesson booking utilities
export * from './lessonBooking';
/** Owner edit flows: warn when adjusting term or camp schedules that are in session */
export * from './lessonEditingScheduleWarning';
export * from './campBookingPricing';
export * from './campDaySelection';

// Region detection utilities
export * from './regionDetection';

// Location detection utilities
export * from './locationDetection';

// Make-up Credit utilities
export * from './makeupCredit';

// Lesson date utilities
export * from './lessonDates';

// Time overlap utilities
export * from './timeOverlap';

// User role utilities
export * from './userRoles';

// Lesson court/coach utilities (multi-court, multi-coach support)
export * from './lessonCourtsCoaches';
export * from './coachAttendanceStats';

// Cross-site theme (marketing site ↔ web app)
export * from './crossSiteTheme';
export * from './academyPublicUrl';
export * from './orgPublicUrl';

// Program pathway ordering
export * from './programProgression';

// Program template import helpers
export * from './programTemplateImport';
export * from './programLessonTemplate';

// Public browse: which term to highlight (current vs upcoming)
export * from './focusTerm';
export * from './publicBookableTerms';
export * from './publicLessonShare';

// Pro-rata term pricing
export * from './proRataPricing';

// Student-initiated private booking
export * from './privateBookingPricing';
export * from './manualStudentInviteExpiry';
export * from './privateBookingLeadTime';

// Coach schedule vs existing lessons (private booking)
export * from './coachLessonSchedule';

// Court hire vs lesson schedule conflicts
export * from './courtScheduleConflicts';

// Public lesson card cover (lesson + program image fallback)
export * from './lessonCoverImage';

// Academy owner student list: Active vs Inactive from attendance recency
export * from './studentListRecency';

// Attendance export (CSV) scope helpers
export * from './attendanceExportScope';

export * from './lessonBillingPrices';
export * from './studentBillingCsvExport';
export * from './manualBillingCsvExport';

// CSV import: student gender normalization
export * from './studentGenderImport';

// Student profile completeness for booking / waitlist skip flows
export * from './studentProfileComplete';

// Duplicate enrollment detection (full term vs drop-in)
export * from './studentLessonBookingConflict';

// Drop-in: next available session date resolution
export * from './dropInSession';

// Enrollment roster: when to show guardian contact
export * from './enrollmentGuardian';

// Academy staff: manual student hard-delete eligibility
export * from './studentDeletion';

// Subscription plan price resolution (region/currency-safe)
export * from './subscriptionPlanPricing';
export * from './subscriptionAccess';
export * from './galleryUrls';

export * from './managedPaymentsEligibility';
export * from './camp';
export * from './campEnrollment';

// Lesson slot / calendar color utilities (web + mobile)
export * from './lessonSlotColors';

// Sentry release naming (Release Health)
export * from './sentryRelease';

// Sentry environment and sampling (deployment-aware)
export * from './sentryEnvironment';
export * from './browserSupport';

// Phone parsing, validation, and E.164 normalization
export * from './phone';

// Auth continue/return URL validation
export * from './authReturnUrl';

export * from './sportValidation';
export * from './planRecommendation';
export * from './starterRequestBooking';

export * from './entitlements';

export * from './membership';
export * from './inventory';
export * from './clubPublicUrl';
export * from './embedOrigins';
export * from './clubOperatingHours';
export * from './courtHireBooking';
export * from './courtManagement';
export * from './timezones';
export * from './onboardingProfileSync';
export * from './operatingProfilePersonalization';
export * from './clubSetupChecklist';
export * from './setupChecklistProgress';
export * from './tierUnlockBadges';
export * from './managementRoutes';
export * from './playEventPricing';
export * from './playEventPairing';
export * from './playEventPairingPriorities';
export * from './playEventPartnerHistory';
export * from './playEventDraw';
export * from './playEventDrawPreview';
export * from './bracketKioskSegments';
export * from './playEventBracketAdvancement';
export * from './playEventVisibility';
export * from './playEventWithdrawal';
export * from './playEventRoundEdit';
export * from './playEventJoin';
export * from './playEventParticipant';
export * from './playEventMatchDisplay';
export * from './playEventMatchCourts';
export * from './playEventSeriesSchedule';
export * from './playEventSeriesRoster';
export * from './playerRating';
